// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios';
import { reactiveSetArray, set } from '../../../services/util/store.helper';
import HourService from '../../../services/api/hour.service';

const requestCache = {};

export default {
  namespaced: true,
  state: {
    loading: false,
    serverTime: null,
    currentShift: null,
    selectedCell: null,
    currentDate: null,
    machines: [],
    cells: [],
    shifts: [],
    downtimeReasons: [],
    userId: '',
    customerId: '',
    username: '',
    dataFromJSON: false,
    intervalfetch: 50,
    config: {},
    theme: {
      isDark: true,
      themes: {
        dark: {
          cardbg: '#15191E',
          subtitlecolor: '#A1A3A5',
          titlecolor: '#FFFFFF',
          error: '#ED5353',
          success: '#21C77C',
        },
        light: {
          cardbg: '#FFFFFF',
          subtitlecolor: '#333333',
          titlecolor: '#15191E',
          error: '#FF5252',
          success: '#21C77C',
        },
      },
    },
  },

  mutations: {
    setLoading: set('loading'),
    setServerTime: set('serverTime'),
    setCurrentShift: set('currentShift'),
    setCurrentDate: set('currentDate'),
    setSelectedCell: set('selectedCell'),
    setMachines: set('machines'),
    setDowntimeReasons: set('downtimeReasons'),
    setMachine: reactiveSetArray('machines'),
    setCells: set('cells'),
    setShifts: set('shifts'),

    setTheme: set('theme'),
    setUserId: set('userId'),
    setCustomerId: set('customerId'),
    setUsername: set('username'),
    setDataFromJSON: set('dataFromJSON'),
    setConfigJson: set('config'),
    setIntervalFetch: set('intervalfetch'),
  },

  actions: {
    getServerTime: async ({ commit }) => {
      try {
        const { data } = await HourService.getCurrentTime();
        if (data && data.results) {
          commit('setServerTime', data.results);
          return data.results;
        }
        return false;
      } catch (e) {
        console.error('Error fetching server time:', e);
        return false;
      }
    },

    getBusinessTime: async ({ commit, dispatch }) => {
      const serverTime = await dispatch('getServerTime');
      if (serverTime) {
        const data = await dispatch(
          'calendar/getBusinessTime',
          serverTime,
          { root: true },
        );
        if (data) {
          commit('setCurrentShift', data.shiftName);
          commit('setCurrentDate', +data.date);
          return true;
        }
      }
      return false;
    },

    getShifts: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        {
          elementName: 'businesshours',
          query: '?sortquery=sortindex==1',
        },
        { root: true },
      );
      if (records && records.length) {
        commit('setShifts', records);
        return records;
      }
      return [];
    },

    allShifts: async ({ dispatch, commit }) => {
      try {
        const records = await dispatch(
          'element/getRecords',
          { elementName: 'businesshours', query: '?sortquery=sortindex==1' },
          { root: true },
        );
        if (records && records.length) {
          commit('setShifts', records);
          return records;
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    getMachines: async ({ dispatch, commit }, query) => {
      try {
        const records = await dispatch(
          'element/getRecords',
          { elementName: 'machine', query },
          { root: true },
        );
        if (records && records.length) {
          commit('setMachines', records);
          const extractedCells = [...new Set(records.map((m) => m.machinecell).filter(Boolean))];
          commit('setCells', extractedCells);
          return records;
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    fetchDowntimeReasons: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        { elementName: 'downtimereasons' },
        { root: true },
      );
      if (records) {
        commit('setDowntimeReasons', records);
        return true;
      }
      return false;
    },

    getReports: async ({ dispatch }, { reportName, payload }) => {
      try {
        const cacheKey = `${reportName}-${JSON.stringify(payload)}`;

        if (requestCache[cacheKey]) {
          return await requestCache[cacheKey];
        }

        const fetchPromise = dispatch('report/executeReport', { reportName, payload }, { root: true })
          .then((data) => ((data && data.reportData) ? data.reportData : (data || false)))
          .catch(() => false);

        requestCache[cacheKey] = fetchPromise;
        const result = await fetchPromise;

        setTimeout(() => { delete requestCache[cacheKey]; }, 10000);

        return result;
      } catch (e) {
        return false;
      }
    },

    getFileData: async (context, filename) => {
      try {
        const baseUrl = process.env.BASE_URL || '/';
        const cleanFilename = filename.startsWith('/') ? filename.slice(1) : filename;
        const response = await axios.get(`${baseUrl}${cleanFilename}`);
        if (response && response.data) return response.data;
        return false;
      } catch (e) {
        return false;
      }
    },
  },

  getters: {
    activeSite: (state, getters, rootState) => {
      let site = rootState.user?.activeSite || rootState.auth?.user?.site_id;

      if (!site) {
        try {
          const swxUser = JSON.parse(localStorage.getItem('user') || '{}');
          site = swxUser?.site_id
            || swxUser?.site?.id || swxUser?.customer?.site_id || swxUser?.activeSiteId;
        } catch (e) { /* ignore */ }
      }

      if (!site && state.machines && state.machines.length > 0) {
        site = state.machines[0].siteId || state.machines[0].siteid;
      }

      return site;
    },

    filteredMachines: ({ machines, selectedCell }) => {
      if (selectedCell) return machines.filter((m) => m.machinecell === selectedCell);
      return machines;
    },

    machinenames: (state) => state.machines.map((m) => m.machinename),

    shiftdate: (state) => {
      if (!state.currentDate) return '';
      const dateStr = state.currentDate.toString();
      if (dateStr.length === 8) {
        return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
      }
      return dateStr;
    },

    firstShift(state) {
      if (state.shifts && state.shifts.length) {
        return state.shifts[0];
      }
      return null;
    },

    activeTheme(state) {
      return (state.theme && state.theme.isDark) ? 'dark' : 'light';
    },
  },
};
